/**
 * Paging rules for the billing tables (Billing History + Future Invoices).
 *
 * On the staff membership page the tables open short (3 past rows, 2 future
 * rows) so Notes and Message History are not pages of scrolling away. Every
 * other place the tables appear (the member's own billing page, the
 * dashboard widget, the invoice-page embeds) keeps the page size it had.
 *
 * A short page must never hide the fact that a family has unpaid invoices:
 * `unpaidChargesOffPage` counts the unpaid charges the current page does not
 * show, so the table can say so and offer to show them.
 */
import { deriveInvoiceDisplayStatus } from "../../../utils/invoiceDisplayStatus";

export const STAFF_HISTORY_PAGE_SIZE = 3;
export const STAFF_FUTURE_PAGE_SIZE = 2;
export const DEFAULT_HISTORY_PAGE_SIZE = 10;
export const DEFAULT_FUTURE_PAGE_SIZE = 5;

const STAFF_MEMBERSHIP_PAGE = "EditMembershipPage";
const BASE_PAGE_SIZES = [5, 10, 25, 50, 100];

export function defaultHistoryPageSize(source?: string): number {
  return source === STAFF_MEMBERSHIP_PAGE ? STAFF_HISTORY_PAGE_SIZE : DEFAULT_HISTORY_PAGE_SIZE;
}

export function defaultFuturePageSize(source?: string): number {
  return source === STAFF_MEMBERSHIP_PAGE ? STAFF_FUTURE_PAGE_SIZE : DEFAULT_FUTURE_PAGE_SIZE;
}

/**
 * The sizes offered in the "Rows" selector: the usual ones, plus the table's
 * own default and whatever size is currently in effect, so the selector can
 * always show the value it is set to.
 */
export function pageSizeOptions(defaultSize: number, currentSize?: number): number[] {
  const sizes = new Set<number>([...BASE_PAGE_SIZES, defaultSize]);
  if (currentSize && currentSize > 0) sizes.add(currentSize);
  return Array.from(sizes).sort((a, b) => a - b);
}

interface LedgerRow {
  status?: string | null;
  display_status?: string | null;
  payment_state?: string | null;
  amount?: number | string | null;
  metadata?: Record<string, any> | null;
}

/** A payment / credit row, by the same test the table uses to color rows. */
export function isCreditRow(row: LedgerRow | null | undefined): boolean {
  const md = row?.metadata || {};
  return Boolean(md.credit || md.type === "credit" || md.display_payment_method);
}

const UNPAID_STATUSES = new Set(["pending", "outstanding", "partially_paid"]);

/**
 * Is this row a charge the family still owes on?
 *
 * Uses the same display rules as the rest of billing: a charge the backend's
 * allocation says the family's money paid (payment_state, account-balance
 * clubs, PM 3ebf2943), one marked "on_account" by an older backend, or one a
 * non-negative balance has already covered, is NOT unpaid, whatever its
 * stored status says.
 */
export function isUnpaidCharge(
  row: LedgerRow | null | undefined,
  membershipBalance: number | string | null | undefined
): boolean {
  if (!row || isCreditRow(row)) return false;
  if (row.display_status === "on_account") return false;
  const derived = deriveInvoiceDisplayStatus(
    { status: row.display_status || row.status, amount: row.amount, payment_state: row.payment_state },
    membershipBalance
  );
  return UNPAID_STATUSES.has(String(derived));
}

export interface UnpaidOffPage {
  /** Unpaid charges that are NOT on the page being shown. */
  count: number;
  /** Rows needed, from the top, for every unpaid charge to be visible. */
  rowsToShowAll: number;
}

export function unpaidChargesOffPage(
  rows: LedgerRow[] | null | undefined,
  firstIndex: number,
  lastIndexExclusive: number,
  membershipBalance: number | string | null | undefined
): UnpaidOffPage {
  let count = 0;
  let deepest = -1;
  (rows || []).forEach((row, index) => {
    if (!isUnpaidCharge(row, membershipBalance)) return;
    deepest = index;
    if (index < firstIndex || index >= lastIndexExclusive) count += 1;
  });
  return { count, rowsToShowAll: deepest + 1 };
}
